import { useQuery } from '@apollo/client';
import {
  ContextBubble,
  ContextBubbleAnimation,
  ContextBubbleBackground,
} from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import moment from 'moment-timezone';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
// need to import to pass to get refrence to theme props in styled components
import { useTheme } from 'styled-components/macro';

import mailboxSvg from 'assets/mailbox.svg';
import { CarouselBackButton } from 'components/CarouselBackButton';
import { IssueIcon } from 'components/IssueIcon';
import { Phrase } from 'components/Phrase';
import { useStringContext } from 'components/StringContext';
import { ReactComponent as CalendarSvg } from 'components/svg/Calendar.svg';
import { ReactComponent as ClockSvg } from 'components/svg/Clock.svg';
import { ReactComponent as InputErrorSvg } from 'components/svg/InputError.svg';
import { ReactComponent as SummaryMobilePhoneSvg } from 'components/svg/SummaryMobilePhone.svg';
import {
  DateTimeFormat,
  IState,
  RepairSummaryCarouselItem,
  RoomIconKey,
} from 'typings';
import {
  IQueryRoomsArgs,
  IRoomsQuery,
  IServiceCodeQuery,
  IServiceCodeQueryVariables,
} from 'typings/GraphQL';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import {
  Heading,
  IssueName,
  RoomName,
  SummaryDetails,
  SummaryItem,
  SummaryItems,
  errorTooltipStyles,
  issueImageStyles,
  navTooltipStyles,
  summaryItemImageStyles,
} from './styles';

const userTimezone = moment.tz.guess();

const roomsQuery = loader('../../queries/rooms.graphql');

const serviceCodeQuery = loader('../../queries/serviceCode.graphql');

const CAROUSEL_ITEM_ID = CarouselItemId.RepairSummary;

export const CarouselItemRepairSummaryTop = () => {
  const {
    addressTimezone = '',
    customerLocationPhone,
    flow,
    selectedIssue,
    selectedRoomIcon,
    selectedRoomKey,
    serviceCodeId = '',
    customerPhone,
    selectedSchedulePreference,
    serviceZoneTimezone,
    street1,
    timeSlot: {
      endTime = '',
      isUnavailable = false,
      label = null,
      startTime = '',
    } = {},
    viewportSize,
  } = useSelector((state: IState) => ({
    addressTimezone: state.userInput.address.timezone,
    currentCarouselItemId: state.serviceCarousel.currentItemId,
    customerLocationPhone: state.userInput.customerLocationPhone,
    customerPhone: state.userInput.customerPhone,
    flow: state.client.flow,
    history: state.serviceCarousel.history,
    isPreviousCustomer: state.userInput.isPreviousCustomer,
    selectedIssue: state.userInput.selectedIssue,
    selectedRoomIcon: state.userInput.selectedRoomIcon,
    selectedRoomKey: state.userInput.selectedRoomKey,
    selectedSchedulePreference: state.userInput.selectedSchedulePreference,
    serviceCodeId: state.userInput.serviceCodeId,
    serviceZoneTimezone: state.userInput.serviceZoneTimezone,
    street1: state.userInput.address.street1,
    timeSlot: state.userInput.selectedTimeSlot,
    viewportSize: state.device.viewportSize,
  }));

  const flowId = flow?.id;

  const flowCarouselItem = tryGetFlowCarouselItem<RepairSummaryCarouselItem>(
    flow,
    CAROUSEL_ITEM_ID,
  );

  const { defaultIconKey: themeDefaultIconKey } = useTheme();

  const defaultIconKey = flowCarouselItem
    ? flowCarouselItem.options.defaultIconKey
    : themeDefaultIconKey;

  const customerTooltipRef = useRef<ContextBubble | null>(null);
  const scheduleTooltipRef = useRef<ContextBubble | null>(null);
  const strings = useStringContext();

  const { data: roomsData } = useQuery<IRoomsQuery, IQueryRoomsArgs>(
    roomsQuery,
    { variables: { flowId } },
  );
  const { data: serviceCodeData } = useQuery<
    IServiceCodeQuery,
    IServiceCodeQueryVariables
  >(serviceCodeQuery, {
    skip: !serviceCodeId,
    variables: {
      id: serviceCodeId,
    },
  });

  //Prioritize defaultIconKey if its not TRADE_OTHER
  const serviceCodeIconKey = (
    defaultIconKey !== RoomIconKey.TRADE_OTHER
      ? defaultIconKey
      : serviceCodeData?.serviceCode?.icon.key ?? defaultIconKey
  ) as RoomIconKey;
  const rooms = roomsData?.rooms || [];
  const selectedRoom = rooms.find(({ key }) => key === selectedRoomKey);
  const selectedRoomName = selectedRoom ? selectedRoom.name : '';
  const selectedRoomNameSubstitute =
    selectedRoomKey &&
    strings[`ISSUE_SPECIFIER.ROOM.${selectedRoomKey.toUpperCase()}.NAME`];
  const subtitle = selectedRoomNameSubstitute || selectedRoomName;

  let issueName = '';

  if (selectedIssue) {
    issueName = selectedIssue.name;
  } else if (selectedRoomIcon) {
    issueName = selectedRoomIcon.name;
  }

  const endTimeString = moment
    .tz(endTime, addressTimezone)
    .format(DateTimeFormat.TIME_END);
  const startDate = moment.tz(startTime, addressTimezone);
  const startTimeString = startDate.format(DateTimeFormat.TIME_START);
  const timezoneAbbreviation = moment.tz(addressTimezone).zoneAbbr();
  const isTimezoneVisible =
    addressTimezone !== serviceZoneTimezone || addressTimezone !== userTimezone;

  const dateString = startDate.format(DateTimeFormat.DATE_HEADER);
  const timeString =
    label ||
    `${startTimeString}-${endTimeString} ${
      isTimezoneVisible ? timezoneAbbreviation : ''
    }`;

  const serviceCodeIconKeyLarge = `${serviceCodeIconKey}_large` as RoomIconKey;
  const hasLargeIcon = Object.values(RoomIconKey).includes(
    serviceCodeIconKeyLarge,
  );

  const phonePriorityList = [customerPhone, customerLocationPhone];
  // We first want to search all phones for one that is mobile. If we couldn't
  // find one, we find the first phone that has a number
  const displayPhone =
    phonePriorityList.find(({ isMobile }) => !!isMobile) ||
    phonePriorityList.find(({ number }) => !!number);
  const displayPhoneNumber = displayPhone ? displayPhone.number : '';

  const handleScheduleInfoClick = () => {
    if (scheduleTooltipRef.current) {
      scheduleTooltipRef.current.doNextAnimation();

      if (customerTooltipRef.current) {
        customerTooltipRef.current.close();
      }
    }
  };

  const handleCustomerInfoClick = () => {
    if (customerTooltipRef.current) {
      customerTooltipRef.current.doNextAnimation();

      if (scheduleTooltipRef.current) {
        scheduleTooltipRef.current.close();
      }
    }
  };

  return (
    <>
      <CarouselBackButton />
      <Heading>
        {selectedSchedulePreference ? (
          <Phrase id="REPAIR_SUMMARY.HEADER.SCHEDULE_PREFERENCE" />
        ) : (
          <Phrase id="REPAIR_SUMMARY.HEADER.DEFAULT" />
        )}
      </Heading>
      <SummaryDetails>
        <IssueIcon
          css={issueImageStyles}
          iconKey={hasLargeIcon ? serviceCodeIconKeyLarge : serviceCodeIconKey}
        />
        <SummaryItems>
          {issueName && selectedRoomName && (
            <>
              <IssueName>{issueName}</IssueName>
              <RoomName>{subtitle}</RoomName>
            </>
          )}
          {!selectedSchedulePreference && (
            <SummaryItem onClick={handleScheduleInfoClick}>
              <CalendarSvg css={summaryItemImageStyles} />
              <span>{dateString}</span>
            </SummaryItem>
          )}
          <SummaryItem as="span" onClick={handleScheduleInfoClick}>
            {isUnavailable ? (
              <>
                <ContextBubble
                  background={ContextBubbleBackground.DANGER}
                  css={errorTooltipStyles}
                  isEnabled={isUnavailable}
                  pointer={{
                    horizontal: viewportSize === 'large' ? 153 : 92,
                    vertical: 'bottom',
                  }}
                >
                  <Phrase id="REPAIR_SUMMARY.ERROR" />
                </ContextBubble>
                <InputErrorSvg css={summaryItemImageStyles} />
              </>
            ) : (
              <ClockSvg css={summaryItemImageStyles} />
            )}
            <span>{timeString}</span>
          </SummaryItem>
          <ContextBubble
            animation={ContextBubbleAnimation.FADE_IN_OUT}
            css={navTooltipStyles}
            isClosableByX={true}
            isOpenByDefault={false}
            pointer={{
              anchor: 'right',
              horizontal: viewportSize === 'large' ? 71 : 86,
              vertical: 'top',
            }}
            reclickBehavior="toggle"
            ref={(contextBubble) =>
              (scheduleTooltipRef.current = contextBubble)
            }
          >
            <Phrase id="REPAIR_SUMMARY.NAV.SCHEDULE.TOOLTIP.HTML" />
          </ContextBubble>
          <ContextBubble
            animation={ContextBubbleAnimation.FADE_IN_OUT}
            css={navTooltipStyles}
            isClosableByX={true}
            isOpenByDefault={false}
            pointer={{
              horizontal: 'center',
              vertical: 'top',
            }}
            reclickBehavior="toggle"
            ref={(contextBubble) =>
              (customerTooltipRef.current = contextBubble)
            }
          >
            <Phrase id="REPAIR_SUMMARY.NAV.CUSTOMER.TOOLTIP.HTML" />
          </ContextBubble>
          <SummaryItem onClick={handleCustomerInfoClick}>
            <img alt="" css={summaryItemImageStyles} src={mailboxSvg} />
            <span>{street1}</span>
          </SummaryItem>
          <SummaryItem onClick={handleCustomerInfoClick}>
            <SummaryMobilePhoneSvg css={summaryItemImageStyles} />
            <span>{displayPhoneNumber}</span>
          </SummaryItem>
        </SummaryItems>
      </SummaryDetails>
    </>
  );
};
